import { ChangeDetectionStrategy, Component } from '@angular/core';
import { LIST_HEADER_ICONS_CONFIG, ListHeaderComponent } from '@celerofinancas/common-domain';

/**
 * Bank balances header, asked for on both the dashboard
 * ([86e2v9e32](https://app.clickup.com/t/86e2v9e32)) and the smart statement
 * ([86e2v9afr](https://app.clickup.com/t/86e2v9afr)).
 *
 * ## Why a wrapper instead of `<list-header>` inline
 *
 * The two screens need the same configuration, and the configuration is not only
 * inputs: `LIST_HEADER_ICONS_CONFIG` has to be provided too. Repeating the provider on
 * each page is how the two drift apart — the statement would keep an icon the
 * dashboard dropped. This is the single place that decides how the header looks for
 * GetNet.
 *
 * ## Why not `bfm-dashboard-header`
 *
 * `ui-reports` ships that one, and it does wrap `list-header` — but it also renders
 * "Olá, {{ first_name }}!" plus a `welcomeText`, which is not what either card asks
 * for and would collide with the page title the shell already draws.
 *
 * ## What is turned off, and why
 *
 * - `showEditIcon`: opens the account-integration flow. Not contracted for GetNet, and
 *   the mock has no endpoint behind it.
 * - `showConciliationError`: the indicator opens the Belvo accounts modal. The persona
 *   has no Belvo integration, so the icon could only ever be a dead end.
 * - `disableSelection` / `viewOnly`: clicking an account filters the screen by it in
 *   the real product. The cards ask for a balances **header**, not a filter, and a
 *   selection with nothing wired to it reads as a broken control.
 *
 * `useCompanyService: false` mirrors what both `bfm-dashboard-header` and
 * `bfm-cash-flow-header` pass: the company already comes from `CompanyState`, and
 * letting the header re-resolve it triggers a second load.
 *
 * ## It needs a liberation to show the total
 *
 * `BfmAccountTotalComponent` gates the consolidated balance on
 * `LIBERATIONS.viewAccountBalances` (23). The mock persona carries it — see
 * `PERSONA_LIBERATIONS` in `getnet-dataset.ts`. Without it the header renders the
 * account pills and hides the total, which looks like a styling bug and is not one.
 */
@Component({
  selector: 'bank-header',
  standalone: true,
  imports: [ListHeaderComponent],
  template: `
    <!-- Bank balances -->
    <list-header
      [useCompanyService]="false"
      [disableSelection]="true"
      [viewOnly]="true"
    />
    <!-- ! Bank balances -->
  `,
  styleUrls: ['./bank-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: LIST_HEADER_ICONS_CONFIG,
      useValue: {
        showEyeIcon: true,
        showConciliationError: false,
        showSeparator: true,
        textTitleTotalAccounts: 'Saldo total',
        showInfoIcon: true,
        showEditIcon: false,
        showBloquedBalance: false,
        disabledClickAccount: true,
      },
    },
  ],
})
export class BankHeaderComponent {}
